// File: src/pages/routingLabs.js
// Interactive labs and drawing helpers for the "Learning to Route" page.

import React, { useEffect, useMemo, useRef, useState } from "react";

function Slider({ label, min, max, step, value, onChange, format }) {
  return (
    <label className="rlp-slider">
      {label}
      <input
        type="range"
        min={min}
        max={max}
        step={step || 1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <output>{format ? format(value) : value}</output>
    </label>
  );
}

/* ----------------------------------------------------------------------- */
/* Geometry helpers                                                         */
/* ----------------------------------------------------------------------- */

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function tourCost(cities, tour) {
  let c = 0;
  for (let i = 0; i < tour.length; i++) {
    c += dist(cities[tour[i]], cities[tour[(i + 1) % tour.length]]);
  }
  return c;
}

function tourEdges(tour) {
  const set = new Set();
  for (let i = 0; i < tour.length; i++) {
    const a = tour[i];
    const b = tour[(i + 1) % tour.length];
    set.add(a < b ? `${a}-${b}` : `${b}-${a}`);
  }
  return set;
}

// Enumerate all tours with city 0 fixed first (n up to 9 is fine).
function allOptima(cities) {
  const n = cities.length;
  const rest = [];
  for (let i = 1; i < n; i++) rest.push(i);
  let best = Infinity;
  let optima = [];
  const perm = (arr, m) => {
    if (m === arr.length) {
      if (arr[0] > arr[arr.length - 1]) return; // skip mirror image
      const tour = [0, ...arr];
      const c = tourCost(cities, tour);
      if (c < best - 1e-6) {
        best = c;
        optima = [tour];
      } else if (Math.abs(c - best) < 1e-6) {
        optima.push(tour);
      }
      return;
    }
    for (let i = m; i < arr.length; i++) {
      [arr[m], arr[i]] = [arr[i], arr[m]];
      perm(arr, m + 1);
      [arr[m], arr[i]] = [arr[i], arr[m]];
    }
  };
  perm(rest, 0);
  return { best, optima };
}

function TourSvg({ cities, tour, width, height, highlightOnly, highlightOther, colorOnly, colorOther }) {
  const edges = [];
  for (let i = 0; i < tour.length; i++) {
    const a = cities[tour[i]];
    const b = cities[tour[(i + 1) % tour.length]];
    const key = tour[i] < tour[(i + 1) % tour.length]
      ? `${tour[i]}-${tour[(i + 1) % tour.length]}`
      : `${tour[(i + 1) % tour.length]}-${tour[i]}`;
    let stroke = "#242424";
    let sw = 1.6;
    if (highlightOnly && highlightOnly.has(key)) {
      stroke = colorOnly || "#b75247";
      sw = 3;
    } else if (highlightOther && highlightOther.has(key)) {
      stroke = colorOther || "#00665e";
      sw = 3;
    }
    edges.push(<line key={key} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={stroke} strokeWidth={sw} />);
  }
  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="tour drawing">
      {edges}
      {cities.map((c, i) => (
        <g key={c.id}>
          <circle cx={c.x} cy={c.y} r={i === 0 ? 9 : 8} fill={i === 0 ? "#242424" : "#bcd0f5"} stroke="#242424" strokeWidth="1" />
          <text x={c.x} y={c.y + 4} textAnchor="middle" fontSize="10" fill={i === 0 ? "#fff" : "#242424"} fontFamily="ui-monospace, Menlo, monospace">
            {c.id}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* ----------------------------------------------------------------------- */
/* The local-search flowchart, drawn like the board                         */
/* ----------------------------------------------------------------------- */

const FLOW_INK = "#242424";
const FLOW_MUTED = "#5a5a5a";

function FlowBox({ x, y, w, h, fill, lines, small }) {
  const lineH = 17;
  const total = lines.length * lineH + (small ? 14 : 0);
  const y0 = y + h / 2 - total / 2 + 12;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill={fill} stroke="rgba(0,0,0,0.18)" />
      {lines.map((t, i) => (
        <text key={i} x={x + w / 2} y={y0 + i * lineH} textAnchor="middle" fontSize="13.5" fill={FLOW_INK}>
          {t}
        </text>
      ))}
      {small && (
        <text x={x + w / 2} y={y0 + lines.length * lineH} textAnchor="middle" fontSize="11" fill={FLOW_MUTED}>
          {small}
        </text>
      )}
    </g>
  );
}

function FlowDiamond({ cx, cy, hw, hh, lines }) {
  const lineH = 15;
  const y0 = cy - ((lines.length - 1) * lineH) / 2 + 4;
  return (
    <g>
      <polygon points={`${cx},${cy - hh} ${cx + hw},${cy} ${cx},${cy + hh} ${cx - hw},${cy}`} fill="#fbf0a6" stroke="rgba(0,0,0,0.18)" />
      {lines.map((t, i) => (
        <text key={i} x={cx} y={y0 + i * lineH} textAnchor="middle" fontSize="12" fill={FLOW_INK}>
          {t}
        </text>
      ))}
    </g>
  );
}

function FlowLabel({ x, y, text, color }) {
  return (
    <text x={x} y={y} textAnchor="middle" fontSize="12" fontWeight="bold" fill={color || FLOW_MUTED} style={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {text}
    </text>
  );
}

// Narration for each build step of the flowchart. The guided variant swaps
// the second line.
const FLOW_STEPS = [
  "Start with any tour, even a bad one.",
  "Change something small: reverse a segment, move a stop, or swap two stops.",
  "Is the tour cheaper than before?",
  "Yes: keep the change.",
  "Out of time yet?",
  "No: go around again, starting from the changed tour.",
  "Sometimes the small change makes the tour worse. Do you have faith?",
  "Yes: keep it anyway. That is how the search escapes a local optimum.",
  "No: undo the change.",
  "Either way, back to the question of time.",
  "Tired: stop, and hand back the best tour seen.",
];
const FLOW_STEPS_GUIDED = FLOW_STEPS.slice();
FLOW_STEPS_GUIDED[1] = "A trained model looks at the current tour and picks which operator to apply; the change is made with that operator.";
FLOW_STEPS_GUIDED[5] = "No: the changed tour goes back to the model, which picks the next operator.";

function LocalSearchFlow({ guided }) {
  const steps = guided ? FLOW_STEPS_GUIDED : FLOW_STEPS;
  const last = steps.length - 1;
  const [step, setStep] = useState(-1); // -1: nothing drawn yet
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const rootRef = useRef(null);

  // Start building when the figure is in view: an IntersectionObserver where
  // available, plus a plain geometry check on mount and on scroll as fallback.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || started) return undefined;
    const begin = () => {
      setStarted(true);
      setStep(0);
      setPlaying(true);
    };
    const inView = () => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.9 && r.bottom > 0;
    };
    if (inView()) {
      begin();
      return undefined;
    }
    let obs = null;
    if (typeof IntersectionObserver !== "undefined") {
      obs = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) begin();
        },
        { threshold: 0.3 }
      );
      obs.observe(el);
    }
    const onScroll = () => {
      if (inView()) begin();
    };
    const scrollers = Array.from(document.querySelectorAll(".main-content-container, .content-area"));
    window.addEventListener("scroll", onScroll, { passive: true });
    scrollers.forEach((n) => n.addEventListener("scroll", onScroll, { passive: true }));
    return () => {
      if (obs) obs.disconnect();
      window.removeEventListener("scroll", onScroll);
      scrollers.forEach((n) => n.removeEventListener("scroll", onScroll));
    };
  }, [started]);

  useEffect(() => {
    if (!playing) return undefined;
    if (step >= last) {
      setPlaying(false);
      return undefined;
    }
    const id = setTimeout(() => setStep((s) => Math.min(last, s + 1)), 1400);
    return () => clearTimeout(id);
  }, [playing, step, last]);

  const arrow = { stroke: FLOW_INK, strokeWidth: 1.6, fill: "none", markerEnd: "url(#rlp-arrowhead)" };
  const g = (stage, children) => (
    <g key={stage} className={`fl${step >= stage ? " on" : ""}${step === stage ? " now" : ""}`}>
      {children}
    </g>
  );
  const replay = () => {
    setStep(0);
    setPlaying(true);
  };
  const narration = step < 0 ? "Scroll on, the loop draws itself." : steps[step];

  return (
    <div className="rlp-flowchart" ref={rootRef}>
      <svg viewBox="0 0 1180 480" role="img" aria-label={guided ? "local search loop with a learned operator choice" : "local search loop"}>
        <defs>
          <marker id="rlp-arrowhead" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse">
            <path d="M0,0 L8,4 L0,8 z" fill={FLOW_INK} />
          </marker>
        </defs>

        {g(0, <FlowBox x={20} y={190} w={130} h={60} fill="#fbf0a6" lines={["Initial bad", "solution"]} />)}

        {g(
          1,
          <>
            <path d="M150,220 L190,220" {...arrow} />
            {guided && (
              <>
                <FlowBox x={190} y={300} w={170} h={70} fill="#c9e3a4" lines={["RL-trained model", "picks the operator"]} small="2-opt, swap, relocate, or-opt" />
                <path d="M275,300 L275,262" {...arrow} />
                <text x={20} y={330} fontSize="12" fill={FLOW_MUTED}>
                  state: current
                </text>
                <text x={20} y={346} fontSize="12" fill={FLOW_MUTED}>
                  solution
                </text>
                <path d="M115,338 L188,338" {...arrow} />
              </>
            )}
            <FlowBox
              x={190}
              y={180}
              w={170}
              h={80}
              fill="#a8e0dc"
              lines={["Change something", "small"]}
              small={guided ? "operator chosen by the model" : "often with randomness"}
            />
          </>
        )}

        {g(
          2,
          <>
            <path d="M360,220 L390,220" {...arrow} />
            <FlowDiamond cx={470} cy={220} hw={80} hh={45} lines={["less cost", "than before?"]} />
          </>
        )}

        {g(
          3,
          <>
            <path d="M550,220 L600,220" {...arrow} />
            <FlowLabel x={575} y={208} text="yes" color="#9e2a2b" />
            <FlowBox x={600} y={190} w={130} h={60} fill="#f5b3b0" lines={["Apply change"]} />
          </>
        )}

        {g(
          4,
          <>
            <path d="M730,220 L770,220" {...arrow} />
            <FlowDiamond cx={830} cy={220} hw={60} hh={40} lines={["tired yet?"]} />
          </>
        )}

        {g(
          5,
          <>
            {guided ? <path d="M830,180 C830,70 170,70 170,330 L188,330" {...arrow} /> : <path d="M830,180 C830,80 275,80 275,178" {...arrow} />}
            <FlowLabel x={552} y={guided ? 62 : 72} text="no" color="#7b3fa0" />
          </>
        )}

        {g(
          6,
          <>
            <path d="M470,265 L470,298" {...arrow} />
            <FlowLabel x={490} y={286} text="no" />
            <FlowDiamond cx={470} cy={340} hw={75} hh={42} lines={["do you", "have faith?"]} />
          </>
        )}

        {g(
          7,
          <>
            <path d="M545,340 C620,340 665,300 665,252" {...arrow} />
            <FlowLabel x={610} y={330} text="yes" color="#00665e" />
          </>
        )}

        {g(
          8,
          <>
            <path d="M470,382 L470,410" {...arrow} />
            <FlowLabel x={490} y={402} text="no" />
            <FlowBox x={405} y={410} w={130} h={50} fill="#f5b3b0" lines={["Revert change"]} />
          </>
        )}

        {g(9, <path d="M535,435 C700,435 830,400 830,262" {...arrow} />)}

        {g(
          10,
          <>
            <path d="M890,220 L940,220" {...arrow} />
            <FlowLabel x={915} y={208} text="yes" />
            <FlowBox x={940} y={190} w={150} h={60} fill="#fbf0a6" lines={["Good enough", "solution"]} />
          </>
        )}
      </svg>
      <div className="rlp-flow-bar">
        <span className="rlp-flow-step">
          {step < 0 ? "" : `step ${step + 1} of ${steps.length}`}
        </span>
        <span className="rlp-flow-narration">{narration}</span>
        <span className="rlp-flow-controls">
          <button type="button" className="rlp-btn" onClick={() => { setPlaying(false); setStep((s) => Math.min(last, Math.max(0, s + 1))); }} disabled={step >= last}>
            Next step
          </button>
          <button type="button" className="rlp-btn" onClick={replay}>
            Replay
          </button>
          <button type="button" className="rlp-btn" onClick={() => { setPlaying(false); setStep(last); }} disabled={step >= last}>
            Show all
          </button>
        </span>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Lab 1 and Lab 4: local search on a 9-city tour                           */
/* ----------------------------------------------------------------------- */

const LS_CITIES = [
  { id: "a", x: 40, y: 60 },
  { id: "b", x: 125, y: 28 },
  { id: "c", x: 225, y: 42 },
  { id: "d", x: 278, y: 112 },
  { id: "e", x: 232, y: 192 },
  { id: "f", x: 150, y: 205 },
  { id: "g", x: 68, y: 175 },
  { id: "h", x: 152, y: 112 },
  { id: "i", x: 30, y: 122 },
];
const LS_START = [0, 4, 2, 6, 1, 7, 3, 8, 5];
const OPERATORS = ["2-opt", "swap", "relocate", "or-opt"];

function kNearest(cities, k) {
  return cities.map((c, i) =>
    cities
      .map((d, j) => ({ j, d: dist(c, d) }))
      .filter((o) => o.j !== i)
      .sort((p, q) => p.d - q.d)
      .slice(0, k)
      .map((o) => o.j)
  );
}
const LS_KNN = kNearest(LS_CITIES, 3);

function randInt(n) {
  return Math.floor(Math.random() * n);
}

function applyOperator(tour, op, guided) {
  const n = tour.length;
  const t = tour.slice();
  if (op === "2-opt") {
    let i = 1 + randInt(n - 2);
    let k;
    if (guided) {
      // choose k so that the new edge (t[i-1], t[k]) joins near neighbours
      const prev = t[i - 1];
      const cands = LS_KNN[prev]
        .map((city) => t.indexOf(city))
        .filter((pos) => pos > i && pos < n);
      k = cands.length ? cands[randInt(cands.length)] : i + randInt(n - i);
    } else {
      k = i + 1 + randInt(n - i - 1);
    }
    if (k >= n) k = n - 1;
    if (k <= i) k = Math.min(n - 1, i + 1);
    const seg = t.slice(i, k + 1).reverse();
    return [...t.slice(0, i), ...seg, ...t.slice(k + 1)];
  }
  if (op === "swap") {
    const i = 1 + randInt(n - 1);
    let j = 1 + randInt(n - 1);
    if (j === i) j = 1 + ((i) % (n - 1));
    [t[i], t[j]] = [t[j], t[i]];
    return t;
  }
  if (op === "relocate") {
    const i = 1 + randInt(n - 1);
    const city = t.splice(i, 1)[0];
    let j = 1 + randInt(n - 1);
    if (guided) {
      const near = LS_KNN[city][randInt(LS_KNN[city].length)];
      j = t.indexOf(near) + 1;
    }
    t.splice(j, 0, city);
    return t;
  }
  // or-opt: move a segment of two cities
  const i = 1 + randInt(n - 2);
  const seg = t.splice(i, 2);
  const j = 1 + randInt(t.length - 1);
  t.splice(j, 0, ...seg);
  return t;
}

function LocalSearchLab({ learned }) {
  const optimum = useMemo(() => allOptima(LS_CITIES), []);
  const initial = () => ({
    tour: LS_START,
    cost: tourCost(LS_CITIES, LS_START),
    best: tourCost(LS_CITIES, LS_START),
    bestTour: LS_START,
    iter: 0,
    accepted: 0,
    faithAccepted: 0,
    reverted: 0,
    simTime: 0,
    log: [],
    q: { "2-opt": 0, swap: 0, relocate: 0, "or-opt": 0 },
    counts: { "2-opt": 0, swap: 0, relocate: 0, "or-opt": 0 },
  });
  const [lab, setLab] = useState(initial);
  const [running, setRunning] = useState(false);
  const [faith, setFaith] = useState(6);
  const [maxIter, setMaxIter] = useState(300);
  const [modelCost, setModelCost] = useState(learned ? 4 : 0);
  const [selector, setSelector] = useState(learned ? "bandit" : "random");
  const [guided, setGuided] = useState(false);
  const settings = useRef({});
  settings.current = { faith, maxIter, modelCost, selector, guided };

  const step = (prev) => {
    const s = settings.current;
    if (prev.iter >= s.maxIter) return prev;
    const usesModel = s.selector === "bandit" || s.guided;
    // choose operator
    let op;
    if (s.selector === "bandit") {
      const eps = 0.2;
      if (Math.random() < eps) op = OPERATORS[randInt(OPERATORS.length)];
      else op = OPERATORS.reduce((a, b) => (prev.q[b] > prev.q[a] ? b : a));
    } else if (s.selector === "2-opt only") {
      op = "2-opt";
    } else {
      op = OPERATORS[randInt(OPERATORS.length)];
    }
    const cand = applyOperator(prev.tour, op, s.guided);
    const c = tourCost(LS_CITIES, cand);
    const delta = c - prev.cost;
    let accept = delta < -1e-9;
    let byFaith = false;
    if (!accept && delta > 1e-9 && s.faith > 0) {
      const p = Math.exp(-delta / s.faith);
      if (Math.random() < p) {
        accept = true;
        byFaith = true;
      }
    }
    const q = { ...prev.q };
    const counts = { ...prev.counts, [op]: prev.counts[op] + 1 };
    // bandit learns the average improvement (positive = cost went down)
    const reward = -delta;
    q[op] = q[op] + (reward - q[op]) / counts[op];
    const line = accept
      ? byFaith
        ? { cls: "faith", text: `#${prev.iter + 1} ${op}: +${delta.toFixed(1)} worse, kept on faith` }
        : { cls: "ok", text: `#${prev.iter + 1} ${op}: ${delta.toFixed(1)}, applied` }
      : { cls: "no", text: `#${prev.iter + 1} ${op}: +${delta.toFixed(1)} worse, reverted` };
    const tour = accept ? cand : prev.tour;
    const cost = accept ? c : prev.cost;
    const improved = cost < prev.best - 1e-9;
    return {
      ...prev,
      tour,
      cost,
      best: improved ? cost : prev.best,
      bestTour: improved ? tour : prev.bestTour,
      iter: prev.iter + 1,
      accepted: prev.accepted + (accept && !byFaith ? 1 : 0),
      faithAccepted: prev.faithAccepted + (byFaith ? 1 : 0),
      reverted: prev.reverted + (accept ? 0 : 1),
      simTime: prev.simTime + 1 + (usesModel ? s.modelCost : 0),
      log: [line, ...prev.log].slice(0, 40),
      q,
      counts,
    };
  };

  // step() reads settings through a ref, so the interval only needs to know whether it runs.
  const stepRef = useRef(step);
  stepRef.current = step;
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      setLab((prev) => {
        if (prev.iter >= settings.current.maxIter) {
          setRunning(false);
          return prev;
        }
        return stepRef.current(prev);
      });
    }, 45);
    return () => clearInterval(id);
  }, [running]);

  const reset = () => {
    setRunning(false);
    setLab(initial());
  };

  const gap = ((lab.best - optimum.best) / optimum.best) * 100;
  const tired = lab.iter >= maxIter;

  return (
    <div className="rlp-lab">
      <div className="rlp-lab-head">
        <span className="rlp-lab-kicker">Lab</span>
        <h4>{learned ? "Let a learner choose the operator" : "Run the loop on nine cities"}</h4>
      </div>
      <p className="rlp-lab-help">
        {learned
          ? "Same loop as before. Now a small learner keeps a running score per operator (the average cost reduction it produced) and picks the best one most of the time. Every model call costs time. Compare the simulated time with the random picker at the same number of iterations."
          : "Press Step to change something small. Green lines are applied improvements, amber are worse changes kept on faith, red are reverted. Faith is the temperature: how willing you are to accept a worse tour to escape a local optimum. Tired is the iteration budget."}
      </p>
      <div className="rlp-lab-grid">
        <div>
          <TourSvg cities={LS_CITIES} tour={lab.tour} width={310} height={235} />
          <div className="rlp-stats" style={{ marginTop: "0.6rem" }}>
            <div className="rlp-stat">
              <b>{lab.cost.toFixed(0)}</b>
              <span>current cost</span>
            </div>
            <div className="rlp-stat">
              <b>{lab.best.toFixed(0)}</b>
              <span>best so far</span>
            </div>
            <div className="rlp-stat">
              <b>{gap.toFixed(1)}%</b>
              <span>gap to optimum ({optimum.best.toFixed(0)})</span>
            </div>
            <div className="rlp-stat">
              <b>{lab.iter}</b>
              <span>iterations</span>
            </div>
            <div className="rlp-stat">
              <b>{lab.simTime}</b>
              <span>simulated time units</span>
            </div>
          </div>
        </div>
        <div>
          <div className="rlp-controls">
            <button type="button" className="primary" onClick={() => setLab((p) => step(p))} disabled={tired}>
              Step: change something small
            </button>
            <button type="button" onClick={() => setRunning((r) => !r)} disabled={tired}>
              {running ? "Pause" : "Run until tired"}
            </button>
            <button type="button" onClick={reset}>
              Reset to bad initial tour
            </button>
          </div>
          <div className="rlp-controls">
            <Slider label="faith" min={0} max={20} value={faith} onChange={setFaith} format={(v) => (v === 0 ? "none" : v)} />
            <Slider label="tired after" min={50} max={1500} step={50} value={maxIter} onChange={setMaxIter} />
          </div>
          <div className="rlp-controls">
            <span style={{ fontSize: "0.82rem" }}>operator picker</span>
            <div className="rlp-toggle">
              {["random", "2-opt only", "bandit"].map((m) => (
                <button key={m} type="button" className={selector === m ? "on" : ""} onClick={() => setSelector(m)}>
                  {m === "bandit" ? "learned (bandit)" : m}
                </button>
              ))}
            </div>
            <label className="rlp-slider">
              <input type="checkbox" checked={guided} onChange={(e) => setGuided(e.target.checked)} />
              guide 2-opt and relocate by near neighbours (a heat map)
            </label>
          </div>
          {(selector === "bandit" || guided) && (
            <div className="rlp-controls">
              <Slider
                label="one model call costs"
                min={0}
                max={20}
                value={modelCost}
                onChange={setModelCost}
                format={(v) => `${v} eval${v === 1 ? "" : "s"}`}
              />
            </div>
          )}
          {selector === "bandit" && (
            <>
              <div className="rlp-bars" style={{ height: 90 }}>
                {OPERATORS.map((op) => {
                  const vals = OPERATORS.map((o) => lab.q[o]);
                  const max = Math.max(1e-6, ...vals.map((v) => Math.abs(v)));
                  const h = Math.max(4, (Math.abs(lab.q[op]) / max) * 60);
                  const best = OPERATORS.reduce((a, b) => (lab.q[b] > lab.q[a] ? b : a));
                  return (
                    <div key={op} className={`rlp-bar${best === op ? " picked" : ""}`}>
                      <i style={{ height: h, opacity: lab.q[op] < 0 ? 0.4 : 1 }} />
                      <span>
                        {op} {lab.q[op].toFixed(1)} ({lab.counts[op]})
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="rlp-lab-help">
                Bars show the learner's average cost reduction per operator and how often each was tried. Negative averages are drawn faded. This is the smallest possible "RL guiding local search": one state, four actions, reward equals cost reduction.
              </p>
            </>
          )}
          <div className="rlp-stats">
            <div className="rlp-stat">
              <b>{lab.accepted}</b>
              <span>improvements applied</span>
            </div>
            <div className="rlp-stat">
              <b>{lab.faithAccepted}</b>
              <span>kept on faith</span>
            </div>
            <div className="rlp-stat">
              <b>{lab.reverted}</b>
              <span>reverted</span>
            </div>
          </div>
          <pre className="rlp-log">
            {lab.log.length === 0
              ? "no moves yet"
              : lab.log.map((l, i) => (
                  <span key={i} className={l.cls}>
                    {l.text}
                    {"\n"}
                  </span>
                ))}
          </pre>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Lab 2: supervised loss on a five-city problem with several optima         */
/* ----------------------------------------------------------------------- */

const SL_PROBLEMS = {
  A: {
    label: "Problem A: cities a, b, c, d, e",
    cities: [
      { id: "a", x: 40, y: 40 },
      { id: "b", x: 200, y: 40 },
      { id: "c", x: 200, y: 200 },
      { id: "d", x: 40, y: 200 },
      { id: "e", x: 120, y: 120 },
    ],
  },
  "A'": {
    label: "Problem A': cities a, b, c, d, f",
    cities: [
      { id: "a", x: 40, y: 40 },
      { id: "b", x: 200, y: 40 },
      { id: "c", x: 200, y: 200 },
      { id: "d", x: 40, y: 200 },
      { id: "f", x: 96, y: 132 },
    ],
  },
};

function tourName(cities, tour) {
  return tour.map((i) => cities[i].id).join("-");
}

function SupervisedLab() {
  const [pk, setPk] = useState("A");
  const [labelIdx, setLabelIdx] = useState(0);
  const [outputIdx, setOutputIdx] = useState(1);
  const problem = SL_PROBLEMS[pk];
  const { best, optima } = useMemo(() => allOptima(problem.cities), [problem]);
  // candidate model outputs: all optima plus two near-misses
  const candidates = useMemo(() => {
    const list = optima.map((t) => ({ tour: t, kind: "optimal" }));
    const n = problem.cities.length;
    const seen = new Set(list.map((o) => tourName(problem.cities, o.tour)));
    const extra = [
      [0, 1, 2, 3, 4],
      [0, 2, 1, 3, 4],
      [0, 1, 3, 2, 4],
    ];
    extra.forEach((t) => {
      if (t.length === n && !seen.has(tourName(problem.cities, t))) {
        list.push({ tour: t, kind: "not optimal" });
        seen.add(tourName(problem.cities, t));
      }
    });
    return list;
  }, [optima, problem]);

  const label = optima[Math.min(labelIdx, optima.length - 1)];
  const out = candidates[Math.min(outputIdx, candidates.length - 1)].tour;
  const labelEdges = tourEdges(label);
  const outEdges = tourEdges(out);
  const missing = new Set([...labelEdges].filter((e) => !outEdges.has(e)));
  const extraEdges = new Set([...outEdges].filter((e) => !labelEdges.has(e)));
  const outCost = tourCost(problem.cities, out);

  const switchProblem = (k) => {
    setPk(k);
    setLabelIdx(0);
    setOutputIdx(1);
  };

  return (
    <div className="rlp-lab">
      <div className="rlp-lab-head">
        <span className="rlp-lab-kicker">Lab</span>
        <h4>The loss punishes difference, not cost</h4>
      </div>
      <p className="rlp-lab-help">
        The labelling solver returns one optimal tour. The model returns a tour. Loss is the number of label edges the model's tour does not contain (the board's example). Change which optimum the labeller happened to return and watch the loss on an equally good tour.
      </p>
      <div className="rlp-controls">
        <div className="rlp-toggle">
          {Object.keys(SL_PROBLEMS).map((k) => (
            <button key={k} type="button" className={pk === k ? "on" : ""} onClick={() => switchProblem(k)}>
              {SL_PROBLEMS[k].label}
            </button>
          ))}
        </div>
      </div>
      <div className="rlp-lab-grid" style={{ gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)" }}>
        <div>
          <p className="rlp-lab-help" style={{ marginBottom: "0.4rem" }}>
            <b>Expected output</b> (label). This problem has {optima.length} optimal tour{optima.length === 1 ? "" : "s"} at cost {best.toFixed(0)}.
          </p>
          <TourSvg cities={problem.cities} tour={label} width={240} height={240} highlightOnly={missing} colorOnly="#b75247" />
          <div className="rlp-controls" style={{ marginTop: "0.5rem" }}>
            <span style={{ fontSize: "0.82rem" }}>labeller returned</span>
            <div className="rlp-toggle">
              {optima.map((t, i) => (
                <button key={i} type="button" className={labelIdx === i ? "on" : ""} onClick={() => setLabelIdx(i)}>
                  {tourName(problem.cities, t)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <p className="rlp-lab-help" style={{ marginBottom: "0.4rem" }}>
            <b>Actual output</b> (model). Cost {outCost.toFixed(0)}, {outCost - best < 1e-6 ? "also optimal" : `${(((outCost - best) / best) * 100).toFixed(1)}% above optimal`}.
          </p>
          <TourSvg cities={problem.cities} tour={out} width={240} height={240} highlightOnly={extraEdges} colorOnly="#00665e" />
          <div className="rlp-controls" style={{ marginTop: "0.5rem" }}>
            <span style={{ fontSize: "0.82rem" }}>model produced</span>
            <div className="rlp-toggle" style={{ flexWrap: "wrap" }}>
              {candidates.map((c, i) => (
                <button key={i} type="button" className={outputIdx === i ? "on" : ""} onClick={() => setOutputIdx(i)}>
                  {tourName(problem.cities, c.tour)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="rlp-formula" style={{ marginTop: "0.8rem" }}>
        loss = label edges missing from output = <b>{missing.size}</b>
        {"   "}(red edges on the left are missing; green edges on the right are the model's replacements)
        {missing.size > 0 && outCost - best < 1e-6 && (
          <>
            {"\n"}
            <b>the model's tour is optimal and the loss is still {missing.size}</b>. The gradient will push it toward the labeller's arbitrary pick.
          </>
        )}
        {missing.size === 0 && (
          <>
            {"\n"}zero loss only when the model reproduces the label exactly.
          </>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Lab 3: the Q-table conversation between You and the Model                */
/* ----------------------------------------------------------------------- */

const Q_STATES = ["I", "II", "III", "IV"];
const Q_ACTIONS = ["A", "B", "C", "D"];
const Q_INIT = [
  [1, 2, 3, 1],
  [3, 2, 5, 1],
  [5, 1, 5, 6],
  [1, 3, 2, 1],
];
// Environment: for state s and action a, [next state index, reward].
// Illustrative, chosen so that the board's story (I, C) -> IV with reward 7 holds.
const Q_ENV = [
  [[1, 1], [2, 2], [3, 7], [0, 0]],
  [[2, 2], [0, 1], [3, 4], [1, 0]],
  [[3, 3], [1, 0], [0, 5], [2, 6]],
  [[0, 1], [1, 3], [2, 2], [3, -1]],
];
const SESSION_LEN = 6;

function softmax(vals, temp) {
  const t = Math.max(0.05, temp);
  const m = Math.max(...vals);
  const ex = vals.map((v) => Math.exp((v - m) / t));
  const s = ex.reduce((a, b) => a + b, 0);
  return ex.map((e) => e / s);
}

function sample(probs) {
  const r = Math.random();
  let acc = 0;
  for (let i = 0; i < probs.length; i++) {
    acc += probs[i];
    if (r < acc) return i;
  }
  return probs.length - 1;
}

function QLab() {
  const [q, setQ] = useState(Q_INIT.map((r) => r.slice()));
  const [state, setState] = useState(0);
  const [pending, setPending] = useState(null); // {s, a, r, s2, target, td, last}
  const [mode, setMode] = useState("exploit");
  const [alpha, setAlpha] = useState(0.5);
  const [gamma, setGamma] = useState(1);
  const [temp, setTemp] = useState(1);
  const [rewards, setRewards] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [touched, setTouched] = useState(null);
  const [mcts, setMcts] = useState(null);
  const [auto, setAuto] = useState(0); // steps left in an automatic run
  const [history, setHistory] = useState([]);
  const [stepCount, setStepCount] = useState(0);

  const probsFor = (s, table) => {
    const row = table[s];
    if (mode === "exploit") {
      const best = row.indexOf(Math.max(...row));
      return row.map((_, i) => (i === best ? 1 : 0));
    }
    if (mode === "explore") {
      const eps = 0.3;
      const best = row.indexOf(Math.max(...row));
      return row.map((_, i) => eps / 4 + (i === best ? 1 - eps : 0));
    }
    return softmax(row, temp);
  };

  // The observed target: reward plus the discounted best value of the next
  // state, except on the last step of a session, where nothing follows.
  const targetFor = (table, s2, r, isLast) => (isLast ? r : r + gamma * Math.max(...table[s2]));

  const describe = (s, a, r, s2, before, after, isLast) =>
    `${Q_STATES[s]} --${Q_ACTIONS[a]}--> ${Q_STATES[s2]}   reward ${r}   Q(${Q_STATES[s]},${Q_ACTIONS[a]}) ${before} -> ${after}${isLast ? "   session ends" : ""}`;

  // One complete learning step on plain values (used by the automatic run).
  const stepOnce = (table, s, rs, probs) => {
    const a = sample(probs);
    const [s2, r] = Q_ENV[s][a];
    const isLast = rs.length + 1 >= SESSION_LEN;
    const before = table[s][a];
    const after = Math.round((before + alpha * (targetFor(table, s2, r, isLast) - before)) * 100) / 100;
    const next = table.map((row) => row.slice());
    next[s][a] = after;
    return { next, a, r, s2, isLast, line: describe(s, a, r, s2, before, after, isLast) };
  };

  const take = (a) => {
    if (pending || auto) return;
    const [s2, r] = Q_ENV[state][a];
    const isLast = rewards.length + 1 >= SESSION_LEN;
    const target = targetFor(q, s2, r, isLast);
    const td = target - q[state][a];
    setPending({ s: state, a, r, s2, target, td, last: isLast });
    setMcts(null);
  };

  const apply = () => {
    if (!pending) return;
    const { s, a, s2, r, td, last } = pending;
    const next = q.map((row) => row.slice());
    const after = Math.round((next[s][a] + alpha * td) * 100) / 100;
    setHistory((h) => [describe(s, a, r, s2, q[s][a], after, last), ...h].slice(0, 8));
    next[s][a] = after;
    setQ(next);
    setTouched(`${s}-${a}`);
    setStepCount((c) => c + 1);
    const rs = [...rewards, r];
    if (last) {
      setSessions((old) => [rs.reduce((x, y) => x + y, 0), ...old].slice(0, 6));
      setRewards([]);
      setState(0);
    } else {
      setRewards(rs);
      setState(s2);
    }
    setPending(null);
  };

  const policyPick = () => {
    if (pending || auto) return;
    take(sample(probsFor(state, q)));
  };

  const runMcts = () => {
    if (pending || auto) return;
    const K = 40;
    const depth = 3;
    const means = Q_ACTIONS.map((_, a) => {
      let total = 0;
      for (let k = 0; k < K; k++) {
        let [s2, r] = Q_ENV[state][a];
        let ret = r;
        let disc = gamma;
        for (let d = 1; d < depth; d++) {
          const p = softmax(q[s2], temp);
          const a2 = sample(p);
          const [s3, r2] = Q_ENV[s2][a2];
          ret += disc * r2;
          disc *= gamma;
          s2 = s3;
        }
        total += ret;
      }
      return total / K;
    });
    setMcts(means);
  };

  // Automatic run: one animated step every 120 ms, so the state, the touched
  // cell and the log visibly move.
  useEffect(() => {
    if (auto <= 0) return undefined;
    const id = setTimeout(() => {
      const { next, r, s2, a, isLast, line } = stepOnce(q, state, rewards, probsFor(state, q));
      setQ(next);
      setTouched(`${state}-${a}`);
      setHistory((h) => [line, ...h].slice(0, 8));
      setStepCount((c) => c + 1);
      if (isLast) {
        const total = [...rewards, r].reduce((x, y) => x + y, 0);
        setSessions((old) => [total, ...old].slice(0, 6));
        setRewards([]);
        setState(0);
      } else {
        setRewards([...rewards, r]);
        setState(s2);
      }
      setAuto((n) => n - 1);
    }, 120);
    return () => clearTimeout(id);
  }, [auto, q, state, rewards, mode, alpha, gamma, temp]);

  const startAuto = (n) => {
    if (pending) return;
    setMcts(null);
    setAuto(n);
  };

  const reset = () => {
    setAuto(0);
    setQ(Q_INIT.map((r) => r.slice()));
    setState(0);
    setPending(null);
    setRewards([]);
    setSessions([]);
    setTouched(null);
    setMcts(null);
    setHistory([]);
    setStepCount(0);
  };

  const probs = probsFor(state, q);
  const bestA = q[state].indexOf(Math.max(...q[state]));
  const busy = !!pending || auto > 0;

  return (
    <div className="rlp-lab">
      <div className="rlp-lab-head">
        <span className="rlp-lab-kicker">Lab</span>
        <h4>You and the Model: a table instead of a deep network</h4>
      </div>
      <p className="rlp-lab-help">
        The Model's estimate of the value of each action in each state is a table (in reality a deep network with the state as input). Click an action in the current row, or let a policy pick one. The environment answers with a reward and a next state. Then fix the Model: move its estimate toward what you observed. A session is {SESSION_LEN} steps; on its last step nothing follows, so the target is just the reward.
      </p>
      <div className="rlp-lab-grid" style={{ gridTemplateColumns: "minmax(280px, 380px) minmax(0, 1fr)" }}>
        <div>
          <table className="rlp-qtable">
            <thead>
              <tr>
                <th className="corner">state \ action</th>
                {Q_ACTIONS.map((a) => (
                  <th key={a}>{a}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Q_STATES.map((sName, s) => (
                <tr key={sName} className={s === state ? "current" : ""}>
                  <th>{sName}</th>
                  {Q_ACTIONS.map((aName, a) => {
                    const cls = [
                      s === state ? "current" : "",
                      s === state && a === bestA ? "best" : "",
                      touched === `${s}-${a}` ? "touched" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <td key={aName} className={cls}>
                        <button type="button" disabled={s !== state || busy} onClick={() => take(a)} title={s === state ? `take action ${aName} in state ${sName}` : ""}>
                          {q[s][a]}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rlp-stats">
            <div className="rlp-stat">
              <b>{Q_STATES[state]}</b>
              <span>current state</span>
            </div>
            <div className="rlp-stat">
              <b>
                {rewards.length} of {SESSION_LEN}
              </b>
              <span>steps into this session</span>
            </div>
            <div className="rlp-stat">
              <b>{rewards.length ? rewards.join(", ") : "none"}</b>
              <span>rewards this session</span>
            </div>
            <div className="rlp-stat">
              <b>{sessions.length ? sessions.join(", ") : "none yet"}</b>
              <span>value of finished sessions</span>
            </div>
            <div className="rlp-stat">
              <b>{stepCount}</b>
              <span>steps taken in total</span>
            </div>
          </div>
          <pre className="rlp-log">
            {history.length === 0 ? "no steps yet" : history.map((l, i) => `${l}\n`).join("")}
          </pre>
        </div>
        <div>
          <div className="rlp-controls">
            <span style={{ fontSize: "0.82rem" }}>policy</span>
            <div className="rlp-toggle">
              {[
                ["exploit", "exploit (greedy)"],
                ["explore", "explore 30% (off-policy)"],
                ["onpolicy", "on-policy (probabilistic)"],
              ].map(([m, lbl]) => (
                <button key={m} type="button" className={mode === m ? "on" : ""} onClick={() => setMode(m)}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="rlp-controls">
            <Slider label="learning rate" min={0.1} max={1} step={0.1} value={alpha} onChange={setAlpha} format={(v) => v.toFixed(1)} />
            <Slider label="discount" min={0.5} max={1} step={0.05} value={gamma} onChange={setGamma} format={(v) => v.toFixed(2)} />
            {mode === "onpolicy" && <Slider label="temperature" min={0.2} max={4} step={0.2} value={temp} onChange={setTemp} format={(v) => v.toFixed(1)} />}
          </div>
          <div className="rlp-bars" style={{ height: 96 }}>
            {Q_ACTIONS.map((a, i) => (
              <div key={a} className={`rlp-bar${probs[i] === Math.max(...probs) ? " picked" : ""}`}>
                <i style={{ height: Math.max(3, probs[i] * 70) }} />
                <span>
                  {a} {(probs[i] * 100).toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
          <p className="rlp-lab-help" style={{ margin: "0 0 0.6rem" }}>
            What the policy would pick in state {Q_STATES[state]}. Exploit follows the table. Explore ignores the table 30% of the time. On-policy turns the row into probabilities and rolls the dice; the Model owns the trade-off. With exploit the run repeats the same few cells, which is exactly the point: it never learns about the others.
          </p>
          <div className="rlp-controls">
            <button type="button" className="primary" onClick={policyPick} disabled={busy}>
              Let the policy choose one step
            </button>
            <button type="button" onClick={runMcts} disabled={busy}>
              Simulate 40 futures per action (MCTS)
            </button>
            {auto > 0 ? (
              <button type="button" onClick={() => setAuto(0)}>
                Stop ({auto} steps left)
              </button>
            ) : (
              <button type="button" onClick={() => startAuto(60)} disabled={!!pending}>
                Auto-run 60 steps, animated
              </button>
            )}
            <button type="button" onClick={reset}>
              Reset table
            </button>
          </div>
          {mcts && (
            <div className="rlp-formula">
              MCTS mean return over 3 steps, 40 rollouts each: {Q_ACTIONS.map((a, i) => `${a} ${mcts[i].toFixed(1)}`).join("   ")}
              {"\n"}best by simulation: <b>{Q_ACTIONS[mcts.indexOf(Math.max(...mcts))]}</b> (the table's greedy pick is {Q_ACTIONS[bestA]}).{" "}
              <button type="button" className="rlp-btn" style={{ marginLeft: "0.5rem" }} onClick={() => take(mcts.indexOf(Math.max(...mcts)))}>
                take it
              </button>
            </div>
          )}
          <div className="rlp-dialogue">
            {!pending && (
              <div className="rlp-bubble">
                <span className="who">Model</span>
                <span className="say">
                  This is what I think will happen to you if you take any of the actions from state {Q_STATES[state]}: {Q_ACTIONS.map((a, i) => `${a} ${q[state][i]}`).join(", ")}.
                </span>
              </div>
            )}
            {pending && (
              <>
                <div className="rlp-bubble">
                  <span className="who">You</span>
                  <span className="say">
                    Ok model, I take action {Q_ACTIONS[pending.a]} in state {Q_STATES[pending.s]}.
                  </span>
                </div>
                <div className="rlp-bubble">
                  <span className="who" style={{ background: "#fbf0a6" }}>
                    Env
                  </span>
                  <span className="say">
                    Observation: state {Q_STATES[pending.s]}, action {Q_ACTIONS[pending.a]}, reward <b>{pending.r}</b>, next state <b>{Q_STATES[pending.s2]}</b>.
                    {pending.last && " That was the last step of the session."}
                  </span>
                </div>
                <div className="rlp-bubble">
                  <span className="who">You</span>
                  <span className="say">
                    {pending.td > 0.01 &&
                      (pending.last
                        ? `Hey model, you were too pessimistic. You thought ${q[pending.s][pending.a]}; I got ${pending.r} and the session is over. Let's fix you a bit.`
                        : `Hey model, you were too pessimistic. You thought ${q[pending.s][pending.a]}; I got ${pending.r} now and from ${Q_STATES[pending.s2]} you yourself say I can still get ${Math.max(...q[pending.s2])}. Let's fix you a bit.`)}
                    {pending.td < -0.01 &&
                      (pending.last
                        ? `Hey model, you were too optimistic. You thought ${q[pending.s][pending.a]}; I got ${pending.r} and the session is over. Let's fix you a bit.`
                        : `Hey model, you were too optimistic. You thought ${q[pending.s][pending.a]}; I got ${pending.r} and from ${Q_STATES[pending.s2]} the best you promise is ${Math.max(...q[pending.s2])}. Let's fix you a bit.`)}
                    {Math.abs(pending.td) <= 0.01 && "Good call, model. Your estimate matched what happened."}
                  </span>
                </div>
                <div className="rlp-formula">
                  what the model thinks: Q({Q_STATES[pending.s]}, {Q_ACTIONS[pending.a]}) = {q[pending.s][pending.a]}
                  {"\n"}what the model observes: {pending.last ? `reward only, the session ends = ${pending.r}` : `reward + discount x best next value = ${pending.r} + ${gamma} x ${Math.max(...q[pending.s2])}`} = <b>{pending.target.toFixed(2)}</b>
                  {"\n"}update: {q[pending.s][pending.a]} + {alpha} x ({pending.target.toFixed(2)} - {q[pending.s][pending.a]}) = <b>{(q[pending.s][pending.a] + alpha * pending.td).toFixed(2)}</b>
                </div>
                <div className="rlp-controls">
                  <button type="button" className="primary" onClick={apply}>
                    Fix the model and {pending.last ? "start a new session" : `move to state ${Q_STATES[pending.s2]}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


export {
  Slider,
  dist,
  tourCost,
  tourEdges,
  allOptima,
  TourSvg,
  FLOW_INK,
  FLOW_MUTED,
  FlowBox,
  FlowDiamond,
  FlowLabel,
  LocalSearchFlow,
  LS_CITIES,
  SL_PROBLEMS,
  LocalSearchLab,
  SupervisedLab,
  QLab,
  softmax,
};
